import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Package, Wrench, Pencil, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';

const conditionLabels = { nuevo:'Nuevo', como_nuevo:'Como nuevo', bueno:'Buen estado', usado:'Usado' };
const statusLabels = { available:'Disponible', sold:'Vendido', traded:'Intercambiado' };
const serviceLocationLabels = { mi_domicilio:'Mi Domicilio', a_domicilio:'A Domicilio', digital:'Digital' };

const getMediaUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${url}`);

export default function LockerPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/items/my').then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const deleteItem = async (itemId) => {
    try { await api.delete(`/items/${itemId}`); setItems(prev => prev.filter(i => i.item_id !== itemId)); toast.success('Eliminado'); }
    catch { toast.error('Error al eliminar'); }
  };

  return (
    <div data-testid="locker-page" className="min-h-screen bg-background page-with-nav">
      <div className="max-w-md mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl text-primary uppercase">MI LOCKER</h1>
            <p className="text-muted-foreground text-sm font-body">{items.length} articulos</p>
          </div>
          <Button data-testid="locker-add-btn" onClick={() => navigate('/upload')} className="rounded-sm font-body font-bold uppercase text-xs bg-primary text-primary-foreground">
            <Plus className="w-4 h-4 mr-1" /> SUBIR
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : items.length === 0 ? (
          <div data-testid="locker-empty" className="text-center py-20 space-y-4">
            <Package className="w-16 h-16 text-muted-foreground mx-auto" />
            <h3 className="font-heading text-xl text-foreground">LOCKER VACIO</h3>
            <p className="text-muted-foreground font-body text-sm">Sube articulos para empezar a negociar</p>
            <Button onClick={() => navigate('/upload')} className="rounded-sm font-body font-bold uppercase">SUBIR PRIMER ARTICULO</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map(item => {
              const firstImg = item.images?.[0] || item.image_url;
              const imgCount = item.images?.length || 0;
              return (
                <div key={item.item_id} data-testid={`locker-item-${item.item_id}`}
                  className="bg-card border-2 border-border rounded-sm overflow-hidden group cursor-pointer"
                  onClick={() => navigate(`/item/${item.item_id}`)}>
                  <div className="aspect-square relative bg-zinc-900">
                    {item.video_url ? (
                      <video src={getMediaUrl(item.video_url)} className="object-cover w-full h-full" muted playsInline preload="metadata" />
                    ) : firstImg ? (
                      <img src={getMediaUrl(firstImg)} className="object-cover w-full h-full" alt={item.title} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-muted-foreground" /></div>
                    )}
                    <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
                      <Badge className={`rounded-sm text-[10px] font-body ${item.status==='available' ? 'bg-green-600/80 text-white border-0' : item.status==='sold' ? 'bg-red-600/80 text-white border-0' : 'bg-zinc-600/80 text-white border-0'}`}>
                        {statusLabels[item.status]||item.status}
                      </Badge>
                      {item.item_type === 'service' && <Badge className="rounded-sm text-[10px] font-body bg-orange-500/80 text-white border-0">Servicio</Badge>}
                      {item.item_type === 'wanted' && <Badge className="rounded-sm text-[10px] font-body bg-[#FFD700]/80 text-black border-0">Se Busca</Badge>}
                      {imgCount > 1 && <Badge className="rounded-sm text-[10px] font-body bg-black/60 text-white border-0">{imgCount}</Badge>}
                    </div>
                    {item.status === 'available' && (
                      <div className="absolute top-2 right-2 flex flex-col gap-1.5">
                        <button data-testid={`locker-edit-${item.item_id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/edit/${item.item_id}`); }}
                          className="bg-primary/90 text-primary-foreground p-1.5 rounded-sm hover:bg-primary transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`locker-delete-${item.item_id}`}
                          onClick={(e) => { e.stopPropagation(); deleteItem(item.item_id); }}
                          className="bg-destructive/80 text-white p-1.5 rounded-sm hover:bg-destructive transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="p-3 space-y-1">
                    <h4 className="font-body font-bold text-foreground text-sm truncate">{item.title}</h4>
                    <p className="font-heading text-lg text-primary">${item.price?.toLocaleString()}{item.item_type === 'service' ? <span className="text-xs text-muted-foreground font-body ml-1">base</span> : null}</p>
                    <p className="text-muted-foreground text-[11px] font-body">
                      {item.item_type === 'service'
                        ? (serviceLocationLabels[item.service_location] || 'Servicio')
                        : (conditionLabels[item.condition] || item.condition)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
